import {
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { FaIconLibrary, FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { NgSelectModule } from "@ng-select/ng-select";
import { TranslatePipe } from "@ngx-translate/core";
import { debounceTime, distinctUntilChanged, Subject } from "rxjs";

import { AdaaHelper } from "../../../../core/utils";
import { ParameterCatalog, ValueText } from "../../../models";
import { LanguageService } from "../../../services";

@Component({
  selector: "adaa-form-dropdown-multi",
  imports: [FontAwesomeModule, ReactiveFormsModule, NgSelectModule, TranslatePipe],
  templateUrl: "./form-dropdown-multi.html",
  styleUrl: "./form-dropdown-multi.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormDropdownMultiComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => FormDropdownMultiComponent),
    },
  ],
})
export class FormDropdownMultiComponent implements OnInit, ControlValueAccessor {
  private readonly _iconLibrary = inject(FaIconLibrary);
  readonly languageService = inject(LanguageService);
  readonly AdaaHelper = AdaaHelper;

  readonly searchChannel$ = new Subject<string>();

  readonly #untilDestroy = AdaaHelper.untilDestroyed();
  readonly #searchChannel = this.searchChannel$
    .asObservable()
    .pipe(debounceTime(500), distinctUntilChanged(), this.#untilDestroy());

  private readonly _requiredFromValidator = signal<boolean>(false);
  private readonly _imperativeInvalid = signal<boolean>(false);
  private _disabledByInput = false;
  private _isProgrammaticUpdate = false;
  private _onValidatorChange: () => void;
  private _onTouched: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _onChange: (value: any) => void;

  readonly inputControl = new FormControl();
  readonly DEFAULT_MAX_SELECT = 999999999;
  readonly ALL_Value = "__ALL__";
  displayOptions: ValueText[] = [];

  readonly showAsRequired = input<boolean>(false);
  readonly required = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly label = input<string>("");
  readonly controlName = input<string | null>(null);
  readonly controlId = input<string | null>(null);
  readonly options = input<ValueText[]>([]);
  readonly searchable = input<boolean>(true);
  readonly clearable = input<boolean>(false);
  readonly maxSelect = input<number>(999999999);
  readonly inputPlaceholder = input<string>("common.form.label.please_select");
  readonly isDisabled = input<boolean>(false);
  readonly allowSelectAll = input<boolean>(false);
  readonly setDefaultValue = input<unknown>();
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] }>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly inputChanges = output<any>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly onSearch = output<any>();

  readonly effectiveControlId = computed(() => this.controlId() ?? this.controlName());
  readonly isRequired = computed(() => this.required() || this._requiredFromValidator());
  readonly isInvalid = computed(() => this.invalid() || this._imperativeInvalid());

  constructor() {
    this._iconLibrary.addIcons(faCircleInfo);

    effect(() => {
      if (this.isDisabled()) {
        this._disabledByInput = true;
        this.inputControl.disable();
      } else if (this._disabledByInput) {
        this._disabledByInput = false;
        this.inputControl.enable();
      }
    });

    effect(() => {
      const value = this.setDefaultValue();
      if (value === undefined) return;
      if (value) {
        untracked(() => this.writeValue(value));
      }
    });

    effect(() => {
      const validatorList = this.setValidator();
      const name = this.controlName();
      if (validatorList?.parameterCatalogs) {
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === name);
        if (field) {
          this._requiredFromValidator.set(field.mandatory === "Y");
        }
      }
    });

    effect(() => {
      this.options();
      this.allowSelectAll();
      untracked(() => this._rebuildDisplayOptions());
    });
  }

  public ngOnInit(): void {
    if (this.searchable()) {
      this.#searchChannel.subscribe({
        next: (term) => this.onSearch.emit(term),
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public writeValue(obj: any): void {
    if (obj === null) {
      this.inputControl.reset();
    }

    this.inputControl.setValue(obj);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  public validate(_value: FormControl): false | { required: boolean } {
    const isRequired =
      (this.isRequired() && this.inputControl.value === null) || (this.isRequired() && this.inputControl.value === "");
    return (
      isRequired && {
        required: true,
      }
    );
  }

  public markInvalid(value: boolean): void {
    this._imperativeInvalid.set(value);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public valueChanged(event: any): void {
    if (this._isProgrammaticUpdate) {
      this._isProgrammaticUpdate = false;
      return;
    }

    if (this.allowSelectAll() && Array.isArray(event) && event.includes(this.ALL_Value)) {
      this._toggleSelectAll();
      return;
    }

    if (this._onChange) this._onChange(event);
    this.inputChanges.emit(event);
  }

  public isAllSelected(): boolean {
    const optionValues = this.options().map((o) => o.value);
    const currentValues: unknown[] = Array.isArray(this.inputControl.value) ? this.inputControl.value : [];
    return optionValues.length > 0 && optionValues.every((v) => currentValues.includes(v));
  }

  private _rebuildDisplayOptions(): void {
    if (!this.allowSelectAll()) {
      this.displayOptions = this.options() ?? [];
      return;
    }
    const selectAllLabel = this.languageService.translate("common.form.label.select_all");
    const allOption: ValueText = { value: this.ALL_Value, text: selectAllLabel };
    this.displayOptions = [allOption, ...(this.options() ?? [])];
  }

  private _toggleSelectAll(): void {
    const optionValues = this.options().map((o) => o.value);
    const currentValues: unknown[] = Array.isArray(this.inputControl.value) ? this.inputControl.value : [];
    const allSelected = optionValues.length > 0 && optionValues.every((v) => currentValues.includes(v));

    const nextValues = allSelected ? [] : optionValues;
    this._isProgrammaticUpdate = true;
    this.inputControl.setValue(nextValues);
    if (this._onChange) this._onChange(nextValues);
    this.inputChanges.emit(nextValues);
  }
}
